import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft, Check, CalendarPlus } from "lucide-react";
import { api } from "../lib/api";
import BookingDialog from "../components/BookingDialog";
import AvailabilityGrid from "../components/AvailabilityGrid";

export default function CourtDetail() {
  const { courtId } = useParams();
  const [court, setCourt] = useState(null);
  const [notFound, setNotFound] = useState(false);
  const [bookingOpen, setBookingOpen] = useState(false);
  const [preset, setPreset] = useState(null);

  useEffect(() => {
    api.get(`/courts/${courtId}`).then((r) => setCourt(r.data)).catch(() => setNotFound(true));
  }, [courtId]);

  if (notFound)
    return (
      <main className="max-w-7xl mx-auto px-6 py-32 text-center">
        <h1 className="font-display uppercase text-4xl">Court not found</h1>
        <Link to="/courts" className="text-[#ccff00] mt-4 inline-block">← Back to courts</Link>
      </main>
    );
  if (!court) return <main className="min-h-[60vh]" />;

  return (
    <main data-testid="court-detail-page">
      <section className="relative h-[55vh] min-h-[400px] flex items-end overflow-hidden">
        <img src={court.image} alt={court.name} className="absolute inset-0 w-full h-full object-cover" />
        <div className="absolute inset-0 bg-black/55" />
        <div className="absolute inset-0 bg-gradient-to-t from-[#0a0a0a] to-transparent" />
        <div className="relative z-10 max-w-7xl mx-auto px-6 lg:px-10 pb-14 w-full">
          <Link to="/courts" data-testid="court-back-link" className="text-white/60 hover:text-[#ccff00] text-sm uppercase tracking-widest inline-flex items-center gap-2 mb-6 transition-colors">
            <ArrowLeft size={14} /> All courts
          </Link>
          <motion.h1 initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} className="font-display uppercase text-4xl sm:text-5xl lg:text-7xl tracking-tight" data-testid="court-detail-name">
            {court.name}
          </motion.h1>
          <div className="flex flex-wrap gap-3 mt-4">
            <span className="text-xs uppercase tracking-widest bg-black/70 backdrop-blur px-4 py-2 rounded-full border border-white/15">{court.surface}</span>
            <span className="text-xs uppercase tracking-widest bg-black/70 backdrop-blur px-4 py-2 rounded-full border border-white/15">{court.type}</span>
            <span className="text-xs uppercase tracking-widest bg-[#ccff00] text-black font-bold px-4 py-2 rounded-full">${court.price}/hr</span>
          </div>
        </div>
      </section>

      <section className="max-w-7xl mx-auto px-6 lg:px-10 py-16 grid grid-cols-1 lg:grid-cols-12 gap-12">
        <motion.div initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 }} className="lg:col-span-7">
          <p className="text-[#ccff00] uppercase tracking-[0.3em] text-sm mb-4">About this court</p>
          <p className="text-white/80 text-lg leading-relaxed">{court.description}</p>
          <div className="mt-10 grid grid-cols-1 sm:grid-cols-2 gap-4">
            {court.features.map((f) => (
              <div key={f} className="flex items-center gap-3 bg-[#0f0f10] border border-[#2a2a2a] rounded-md px-5 py-4">
                <Check className="text-[#ccff00] shrink-0" size={18} />
                <span className="text-white/80 text-sm">{f}</span>
              </div>
            ))}
          </div>
        </motion.div>
        <motion.div initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.2 }} className="lg:col-span-5">
          <div className="bg-[#0f0f10] border border-[#ccff00]/40 shadow-[0_0_20px_rgba(204,255,0,0.12)] rounded-md p-8 sticky top-28">
            <p className="font-display uppercase text-2xl tracking-tight">Book this court</p>
            <p className="text-white/60 mt-3 leading-relaxed">
              Pick a date and hour slot. Bookings are simulated — no payment, instant confirmation.
            </p>
            <p className="mt-6">
              <span className="font-display text-5xl text-[#ccff00]">${court.price}</span>
              <span className="text-white/50 text-sm"> / hour</span>
            </p>
            <button
              onClick={() => {
                setPreset(null);
                setBookingOpen(true);
              }}
              data-testid="court-book-slot-btn"
              className="mt-8 w-full bg-[#ccff00] text-black font-bold uppercase tracking-wider py-4 rounded-full hover:bg-[#a3e635] hover:-translate-y-0.5 transition-transform duration-200 inline-flex items-center justify-center gap-2"
            >
              <CalendarPlus size={18} /> Book a slot
            </button>
          </div>
        </motion.div>
      </section>

      <section className="max-w-7xl mx-auto px-6 lg:px-10 pb-20">
        <AvailabilityGrid
          courtId={court.id}
          refreshKey={bookingOpen}
          onPick={(date, time) => {
            setPreset({ date, time });
            setBookingOpen(true);
          }}
        />
      </section>

      <BookingDialog court={court} open={bookingOpen} onOpenChange={setBookingOpen} preset={preset} />
    </main>
  );
}
